import type { AgentView, DeliveryView } from "@walkie/protocol"
import { agentColor } from "../lib/agents"
import {
	deliveryState,
	glyphFor,
	hookOnly,
	labelFor,
	latencyMs,
	presentLine,
	type DState,
} from "../lib/delivery"
import { dur, fullTime } from "../lib/time"
import { focusAgent } from "../store/actions"
import { useStore } from "../store/store"
import { Tip } from "./ui"

const ORDER: DState[] = ["failed", "accepted", "delivered", "presented", "answered"]

/** One glyph per recipient agent, in that agent's colour; shape carries the state. */
export function DeliveryGlyphs({ deliveries }: { deliveries: DeliveryView[] }) {
	const agents = useStore(s => s.agents)
	const now = useStore(s => s.now)
	if (!deliveries.length) return null
	const rows = deliveries
		.map(d => ({ d, a: agents[d.agentId], st: deliveryState(d, now) }))
		.sort((x, y) => ORDER.indexOf(x.st) - ORDER.indexOf(y.st))
	const presented = rows.filter(r => r.st === "presented" || r.st === "answered").length
	return (
		<span className="row" style={{ gap: 4 }}>
			<span className="dglyphs" role="list" aria-label="Delivery per recipient">
				{rows.map(({ d, a, st }) => (
					<Tip
						key={d.agentId}
						content={() => <DeliveryTip d={d} a={a} st={st} now={now} />}
					>
						<button
							role="listitem"
							className={"dg " + st + (hookOnly(d) ? " hook" : "")}
							style={{ color: a ? agentColor(a.color) : undefined }}
							aria-label={`@${a?.handle ?? "agent"}: ${labelFor(d, st, now)}${latencyMs(d) !== null ? `, ${dur(latencyMs(d))}` : ""}`}
							onClick={() => a && focusAgent(a.id)}
						>
							{glyphFor(d, st)}
						</button>
					</Tip>
				))}
			</span>
			{rows.length > 1 && (
				<span className="dsum" aria-hidden>
					{presented}/{rows.length}
				</span>
			)}
		</span>
	)
}

function DeliveryTip({
	d,
	a,
	st,
	now,
}: {
	d: DeliveryView
	a: AgentView | undefined
	st: DState
	now: number
}) {
	const lat = latencyMs(d)
	const line = presentLine(d, a)
	const hook = hookOnly(d)
	return (
		<div>
			<div style={{ marginBottom: 4 }}>
				<b style={{ color: a ? agentColor(a.color) : undefined }}>
					@{a?.handle ?? d.agentId.slice(0, 8)}
				</b>{" "}
				<span className="dim2">
					{glyphFor(d, st)} {labelFor(d, st, now)}
				</span>
			</div>
			{line && <div className={"dtip-line" + (hook ? " hook" : "")}>{line}</div>}
			<table>
				<tbody>
					<tr>
						<td>reason</td>
						<td>
							{d.reason}
							{d.wake ? " · wake" : ""}
						</td>
					</tr>
					<tr>
						<td>accepted</td>
						<td className="mono">{fullTime(d.acceptedAt)}</td>
					</tr>
					<tr>
						<td>delivered</td>
						<td className="mono">{fullTime(d.deliveredAt)}</td>
					</tr>
					<tr>
						<td>presented</td>
						<td className="mono">{fullTime(d.presentedAt)}</td>
					</tr>
					{d.answeredAt && (
						<tr>
							<td>answered</td>
							<td className="mono">{fullTime(d.answeredAt)}</td>
						</tr>
					)}
					<tr>
						<td>latency</td>
						<td className="mono">{lat === null ? "—" : `${Math.round(lat)} ms`}</td>
					</tr>
					{d.attempts > 1 && (
						<tr>
							<td>attempts</td>
							<td className="mono">{d.attempts}</td>
						</tr>
					)}
					{d.lastError && (
						<tr>
							<td>error</td>
							<td style={{ color: "var(--danger)", whiteSpace: "normal" }}>
								{d.lastError}
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	)
}
